import authService from './authService';
import { getApiUrl } from '../utils';

async function makeAuthenticatedRequest(endpoint, options = {}) {
  const token = authService.getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const res = await fetch(`${getApiUrl()}/api/admin/license${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers
    }
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = body.error || `Request failed with status ${res.status}`;
    throw new Error(msg);
  }
  return body;
}

// Legacy activation method (kept for backwards compatibility)
async function signActivation(request) {
  return makeAuthenticatedRequest('/activate', {
    method: 'POST',
    body: JSON.stringify(request)
  });
}

// Process enrollment challenge from an offline appliance
async function processEnrollChallenge(customerProxyTeamId, challengeBlob, instanceName) {
  return makeAuthenticatedRequest('/enrol', {
    method: 'POST',
    body: JSON.stringify({ customerProxyTeamId, challengeBlob, instanceName })
  });
}

// Issue a new license grant
async function issueGrant({
  customerProxyTeamId,
  productId,
  seats,
  validFrom,
  validUntil,
  features,
  scopes,
  notes,
  applianceId,
  challengeBlob,
  applianceNickname
}) {
  return makeAuthenticatedRequest('/grant', {
    method: 'POST',
    body: JSON.stringify({
      customerProxyTeamId,
      productId,
      seats,
      validFrom,
      validUntil,
      features,
      scopes,
      notes,
      applianceId,
      challengeBlob,
      applianceNickname
    })
  });
}

// List appliances for a customer
async function listAppliances(customerProxyTeamId) {
  return makeAuthenticatedRequest(`/appliances?customerProxyTeamId=${encodeURIComponent(customerProxyTeamId)}`);
}

// Update appliance status
async function updateApplianceStatus(applianceId, status) {
  return makeAuthenticatedRequest(`/appliances/${applianceId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status })
  });
}

// Rename an appliance
async function updateApplianceName(applianceId, instanceName) {
  return makeAuthenticatedRequest(`/appliances/${applianceId}/name`, {
    method: 'PATCH',
    body: JSON.stringify({ instanceName })
  });
}

// List grants with optional filters
async function listGrants(customerProxyTeamId, filters = {}) {
  const params = new URLSearchParams();
  if (customerProxyTeamId) params.append('customerProxyTeamId', customerProxyTeamId);
  if (filters.status) params.append('status', filters.status);
  if (filters.productId) params.append('productId', filters.productId);
  const query = params.toString() ? `?${params.toString()}` : '';
  return makeAuthenticatedRequest(`/grants${query}`);
}

// Get a specific grant
async function getGrant(grantId) {
  return makeAuthenticatedRequest(`/grants/${grantId}`);
}

// Revoke a grant
async function revokeGrant(grantId) {
  return makeAuthenticatedRequest(`/grants/${grantId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status: 'revoked' })
  });
}

// Get grants expiring within N days (optionally filtered by customer)
async function getExpiringGrants(daysThreshold = 60, customerProxyTeamId = null) {
  const params = new URLSearchParams({ days: daysThreshold });
  if (customerProxyTeamId) params.append('customerProxyTeamId', customerProxyTeamId);
  return makeAuthenticatedRequest(`/grants/expiring?${params.toString()}`);
}

// Dismiss expiry banner for a grant
async function dismissExpiryBanner(grantId) {
  return makeAuthenticatedRequest(`/grants/${grantId}/dismiss`, {
    method: 'POST'
  });
}

// List all customers with their offline grant status
// Returns has_offline_grants (computed from LicenseGrants, not stored)
async function listCustomers() {
  return makeAuthenticatedRequest('/customers');
}

// List customers that have active offline grants
async function listOfflineCustomers() {
  return makeAuthenticatedRequest('/customers/offline');
}

// Download grant as .rigrlic file (base64-encoded JSON)
// To inspect: base64 -d file.rigrlic | jq .
// See license-manager/docs/rigrlic-format.md for format documentation
function downloadGrantFile(signedBlob, filename) {
  // Keep the base64 encoding - License Manager UI expects base64 and decodes it
  const blob = new Blob([signedBlob], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const licenseService = {
  signActivation,
  processEnrollChallenge,
  issueGrant,
  listAppliances,
  updateApplianceStatus,
  updateApplianceName,
  listGrants,
  getGrant,
  revokeGrant,
  getExpiringGrants,
  dismissExpiryBanner,
  listCustomers,
  listOfflineCustomers,
  downloadGrantFile
};

export default licenseService;
